<template>
  <div class="grid gap-6">
    <div>
      <h3 class="text-sm font-semibold text-obsidian-700">
        {{ t('uninvoicedBookings') }}
      </h3>
    </div>
    <div>
      <div :class="table({ class: 'grid-cols-[repeat(2,minmax(max-content,1fr))]' })">
        <div :class="tableHeader()">
          <div :class="tableCell()">{{ t('booking') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('amountToInvoice') }}</div>
        </div>
        <div
          v-for="item in problems?.uninvoicedBookings ?? []"
          :key="item.bookingId"
          :class="tableRow()"
        >
          <div :class="tableCell()">
            <router-link
              :to="{ name: 'bookingEdit', params: { id: item.bookingId } }"
              class="text-link"
            >
              {{ item.bookingNumber ?? '-' }}
            </router-link>
          </div>
          <div :class="tableCell({ numeric: true })">
            {{ formatCurrency(item.amountToInvoice) }}
          </div>
        </div>
      </div>
      <div class="px-4">
        <EntityListExceptions
          :status="problemsStatus"
          :error="problemsError"
          :items-count="problems?.uninvoicedBookings?.length ?? 0"
          :no-items-text="t('noUninvoicedBookings')"
          small
        />
      </div>
    </div>

    <div>
      <h3 class="text-sm font-semibold text-obsidian-700">
        {{ t('unallocatedPayments') }}
      </h3>
    </div>
    <div class="overflow-hidden">
      <div :class="table({ class: 'grid-cols-[repeat(7,minmax(max-content,1fr))]' })">
        <div :class="tableHeader()">
          <div :class="tableCell()">{{ t('booking') }}</div>
          <div :class="tableCell()">{{ t('invoice') }}</div>
          <div :class="tableCell()">{{ t('agency') }}</div>
          <div :class="tableCell()">{{ t('customer') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('paidAmount') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('allocatedAmount') }}</div>
          <div :class="tableCell({ numeric: true })">{{ t('unallocatedAmount') }}</div>
        </div>
        <div
          v-for="item in problems?.unallocatedPayments ?? []"
          :key="item.paymentId"
          :class="tableRow({ class: 'group/row' })"
        >
          <div :class="tableCell()">
            <router-link
              v-if="item.bookingId"
              :to="{ name: 'bookingEdit', params: { id: item.bookingId } }"
              class="text-link"
            >
              {{ item.bookingNumber ?? '-' }}
            </router-link>
            <router-link
              v-else-if="!item.invoiceId && $can('get', 'Payment')"
              :to="{ name: 'payment', params: { id: item.paymentId } }"
              class="text-link"
            >
              {{ t('payment') }}
            </router-link>
            <span v-else>-</span>
          </div>
          <div :class="tableCell()">
            <div class="flex items-center gap-2">
              <router-link
                v-if="item.invoiceId"
                :to="{ name: 'invoice', params: { id: item.invoiceId } }"
                class="text-link"
              >
                {{ item.completeInvoiceNumber ?? '-' }}
              </router-link>
              <span v-else>-</span>
              <FloatingComments
                v-if="item.invoiceId"
                entity-type="Invoice"
                :entity-id="item.invoiceId"
              />
            </div>
          </div>
          <div :class="tableCell()">
            {{ item.agencyName ?? '-' }}
          </div>
          <div :class="tableCell()">
            {{ item.customerName ?? '-' }}
          </div>
          <div :class="tableCell({ numeric: true })">{{ formatCurrency(item.amount) }}</div>
          <div :class="tableCell({ numeric: true })">
            {{ formatCurrency(item.allocatedAmount) }}
          </div>
          <div :class="tableCell({ numeric: true })">
            {{ formatCurrency(item.unallocatedAmount) }}
          </div>
        </div>
      </div>
    </div>
    <div class="px-4">
      <EntityListExceptions
        :status="problemsStatus"
        :error="problemsError"
        :items-count="problems?.unallocatedPayments?.length ?? 0"
        :no-items-text="t('noUnallocatedPayments')"
        small
      />
    </div>
  </div>

  <div>
    <h3 class="text-sm font-semibold text-obsidian-700">
      {{ t('mismatchingBookingInvoices') }}
    </h3>
  </div>
  <div :class="table({ class: 'grid-cols-[repeat(5,minmax(max-content,1fr))]' })">
    <div :class="tableHeader()">
      <div :class="tableCell()">{{ t('booking') }}</div>
      <div :class="tableCell()">{{ t('invoice') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('positionsAmount') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('allocatedAmount') }}</div>
      <div :class="tableCell({ numeric: true })">{{ t('differenceAmount') }}</div>
    </div>
    <div
      v-for="item in problems?.mismatchingBookingInvoices ?? []"
      :key="item.invoiceId"
      :class="tableRow({ class: 'group/row' })"
    >
      <div :class="tableCell()">
        <router-link
          v-if="item.bookingId"
          :to="{ name: 'bookingEdit', params: { id: item.bookingId } }"
          class="text-link"
        >
          {{ item.bookingNumber ?? '-' }}
        </router-link>
        <span v-else>-</span>
      </div>
      <div :class="tableCell()">
        <div class="flex items-center gap-2">
          <router-link :to="{ name: 'invoice', params: { id: item.invoiceId } }" class="text-link">
            {{ item.completeInvoiceNumber ?? '-' }}
          </router-link>
          <FloatingComments entity-type="Invoice" :entity-id="item.invoiceId" />
        </div>
      </div>
      <div :class="tableCell({ numeric: true })">{{ formatCurrency(item.positionsAmount) }}</div>
      <div :class="tableCell({ numeric: true })">{{ formatCurrency(item.allocatedAmount) }}</div>
      <div :class="tableCell({ numeric: true })">{{ formatCurrency(item.differenceAmount) }}</div>
    </div>
  </div>
  <div class="px-4">
    <EntityListExceptions
      :status="problemsStatus"
      :error="problemsError"
      :items-count="problems?.mismatchingBookingInvoices?.length ?? 0"
      :no-items-text="t('noMismatchingBookingInvoices')"
      small
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import EntityListExceptions from '@/components/EntityListExceptions.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { table, tableCell, tableHeader, tableRow } from '@carvia/theme/lib/cva/grid';
import type { ReceivablesProblemsDto } from '@carvia/ros-client-types';
import FloatingComments from '@/components/comments/FloatingComments.vue';

defineProps<{
  problems?: ReceivablesProblemsDto;
  problemsStatus: 'success' | 'pending' | 'error';
  problemsError: unknown;
}>();

const { t } = useI18n();
</script>

<i18n lang="json">
{
  "en": {
    "uninvoicedBookings": "Uninvoiced bookings",
    "unallocatedPayments": "Unallocated payments",
    "mismatchingBookingInvoices": "Mismatching booking invoices",
    "amountToInvoice": "Amount to invoice",
    "positionsAmount": "Positions",
    "paidAmount": "Paid",
    "allocatedAmount": "Allocated",
    "unallocatedAmount": "Unallocated",
    "differenceAmount": "Difference",
    "noUninvoicedBookings": "No uninvoiced bookings",
    "noUnallocatedPayments": "No unallocated payments",
    "noMismatchingBookingInvoices": "No mismatching booking invoices",
    "booking": "Booking",
    "invoice": "Invoice",
    "agency": "Agency",
    "customer": "Customer",
    "payment": "Payment"
  },
  "de": {
    "uninvoicedBookings": "Nicht fakturierte Buchungen",
    "unallocatedPayments": "Nicht zugeordnete Zahlungen",
    "mismatchingBookingInvoices": "Abweichende Buchungsrechnungen",
    "amountToInvoice": "Zu fakturieren",
    "positionsAmount": "Positionen",
    "paidAmount": "Bezahlt",
    "allocatedAmount": "Zugeordnet",
    "unallocatedAmount": "Nicht zugeordnet",
    "differenceAmount": "Differenz",
    "noUninvoicedBookings": "Keine nicht fakturierten Buchungen",
    "noUnallocatedPayments": "Keine nicht zugeordneten Zahlungen",
    "noMismatchingBookingInvoices": "Keine abweichenden Buchungsrechnungen",
    "booking": "Buchung",
    "invoice": "Rechnung",
    "agency": "Vermittler",
    "customer": "Kunde",
    "payment": "Zahlung"
  },
  "es": {
    "uninvoicedBookings": "Reservas sin facturar",
    "unallocatedPayments": "Pagos sin imputar",
    "mismatchingBookingInvoices": "Facturas de reserva con discrepancia",
    "amountToInvoice": "Importe a facturar",
    "positionsAmount": "Líneas",
    "paidAmount": "Pagado",
    "allocatedAmount": "Imputado",
    "unallocatedAmount": "Sin imputar",
    "differenceAmount": "Diferencia",
    "noUninvoicedBookings": "No hay reservas sin facturar",
    "noUnallocatedPayments": "No hay pagos sin imputar",
    "noMismatchingBookingInvoices": "No hay facturas con discrepancia",
    "booking": "Reserva",
    "invoice": "Factura",
    "agency": "Agencia",
    "customer": "Cliente",
    "payment": "Pago"
  }
}
</i18n>
